import {
  aChainRule,
  aChainRuleExpressionAndLimit,
  aFirstSeenRule,
  anAggregationRule,
  anAggregationRuleAggregationFunction,
  anIntRuleOverride,
  anOutlierRule,
  anOutlierRuleAggregationFunction,
  anOutputEntitySelectorType,
  aRuleScoreMapping,
  aRuleStatus,
  aTemplatedMatchRule,
  aThresholdRule,
  BaselineStatus,
  FirstSeenRuleBaselineType,
  mockRuleBuilderQueryOperationQuery,
  RuleStatusEnum,
} from '__generated__';
import { delay, HttpResponse } from 'msw';

import { ONE_DAY_MILLISECONDS } from '@sbu/utils.time';

export const ENTITY_NAMES = ['device_ip'];
export const RULE_TAGS = [{ key: 'Tactic', value: 'TA0009 - Collection' }];

export const SHARED_RULE_DATA = {
  description: 'some test description',
  descriptionExpression: 'some test description',
  enabled: true,
  entitySelectors: [
    anOutputEntitySelectorType({
      expression: 'device_ip',
      entityType: '_ip',
    }),
  ],
  entitySelectorsOverride: null,
  ruleSource: 'user',
  status: aRuleStatus({ status: RuleStatusEnum.Active }),
  summaryExpression: 'test rule summary',
  summaryExpressionOverride: null,
  tags: ['_mitreAttackTactic:TA0009'],
  score: 5,
  nameExpression: 'test signal name',
  suppressionWindowHours: 23,
  suppressionWindowMinutes: 60,
  suppressionWindowSize: 86400000,
};

export const CHAIN_RULE_DATA = {
  ruleId: 'CHAIN-1',
  filterExpression1:
    "metadata_vendor = 'Bro' and metadata_deviceEventId = 'ssh'",
  filterExpression2:
    "metadata_vendor = 'rob' and metadata_deviceEventId = 'hss'",
  windowSizeInDays: 4,
  windowSizeMilliseconds: 345600000,
  name: `${Date.now()} Test Chain Rule`,
  updatedName: `${Date.now()} Test Chain Rule Updated`,
  updatedScore: 8,
  ruleType: 'chain',
  suppressionWindowSize: 604800000,
  suppressionWindowHours: 168,
  suppressionWindowMinutes: 0,
};

export const MATCH_RULE_DATA = {
  ruleId: 'MATCH-1',
  filterExpression:
    "metadata_vendor = 'Bro' and metadata_deviceEventId = 'ssh'",
  name: `${Date.now()} Test Match Rule`,
  updatedName: `${Date.now()} Test Match Rule Updated`,
  updatedScore: 8,
  ruleType: 'templated match',
  descriptionExpressionOverride: undefined,
};

export const THRESHOLD_RULE_DATA = {
  ruleId: 'THRESHOLD-1',
  filterExpression:
    "metadata_vendor = 'Bro' and metadata_deviceEventId = 'ssh'",
  distinctCount: 3,
  windowSizeInMinutes: 10,
  windowSizeInMilliseconds: 600000,
  name: `${Date.now()} Test Threshold Rule`,
  updatedName: `${Date.now()} Test Threshold Rule Updated`,
  updatedScore: 8,
  ruleType: 'threshold',
};

export const AGGREGATION_RULE_DATA = {
  ruleId: 'AGGREGATION-1',
  matchExpression1:
    "metadata_vendor = 'Bro' and metadata_deviceEventId = 'ssh'",
  matchExpression2: 'bro_dhcp_assignedIp',
  functionExpression: 'accountId = "abc"',
  windowSizeInMinutes: 10,
  windowSizeInMilliseconds: 600000,
  name: `${Date.now()} Test Aggregation Rule`,
  updatedName: `${Date.now()} Test Aggregation Rule Updated`,
  updatedScore: 8,
  ruleType: 'aggregation',
};

export const FIRST_SEEN_RULE_DATA = {
  ruleId: 'FIRST-1',
  ruleType: 'first seen',
  name: 'IT Test First Seen Rule',
  updatedName: 'IT Test First Seen Rule Updated',
  updatedScore: 8,
  baselineWindowSize: 35,
  retentionWindowSize: 85,
  filterExpression: 'objectType="authentication" and success=true',
  valueFields: ['device_ip'],
  baselineType: FirstSeenRuleBaselineType.Global,
  updatedBaselineType: FirstSeenRuleBaselineType.PerEntity,
  groupByFields: ['device_ip'],
};

export const OUTLIER_RULE_DATA = {
  ruleId: 'OUTLIER-1',
  ruleType: 'outlier',
  name: 'IT Test Outlier Rule',
  updatedName: 'IT Test Outlier Rule Updated',
  updatedScore: 8,
  matchExpression: 'objectType="Network"',
  groupByFields: ['device_ip'],
  aggregationFunction: 'count',
  aggregationFunctionArg: 'true',
  updatedAggregationFunction: 'avg',
  updatedAggregationFunctionArg: 'user_role',
  windowSize: ONE_DAY_MILLISECONDS,
  baselineWindowSize: 30,
  retentionWindowSize: 90,
  updatedBaselineWindowSize: 20,
  updatedRetentionWindowSize: 80,
};

const chainRuleData = {
  ...SHARED_RULE_DATA,
  expressionsAndLimits: [
    aChainRuleExpressionAndLimit({
      expression: CHAIN_RULE_DATA.filterExpression1,
      limit: 1,
    }),
    aChainRuleExpressionAndLimit({
      expression: CHAIN_RULE_DATA.filterExpression2,
      limit: 1,
    }),
  ],
  id: CHAIN_RULE_DATA.ruleId,
  name: CHAIN_RULE_DATA.name,
  ruleType: CHAIN_RULE_DATA.ruleType,
  suppressionWindowSize: 604800000,
  suppressionWindowHours: 168,
  tuningExpressions: [],
};

const matchRuleData = {
  ...SHARED_RULE_DATA,
  id: MATCH_RULE_DATA.ruleId,
  ruleType: MATCH_RULE_DATA.ruleType,
  name: MATCH_RULE_DATA.name,
  tuningExpressions: [],
  descriptionExpressionOverride: null,
  expression: MATCH_RULE_DATA.filterExpression,
};

const thresholdRuleData = {
  ...SHARED_RULE_DATA,
  id: THRESHOLD_RULE_DATA.ruleId,
  ruleType: THRESHOLD_RULE_DATA.ruleType,
  name: THRESHOLD_RULE_DATA.name,
  limit: THRESHOLD_RULE_DATA.distinctCount,
  expression: THRESHOLD_RULE_DATA.filterExpression,
};

const aggregationRuleData = {
  ...SHARED_RULE_DATA,
  id: AGGREGATION_RULE_DATA.ruleId,
  ruleType: AGGREGATION_RULE_DATA.ruleType,
  name: AGGREGATION_RULE_DATA.name,
  matchExpression: AGGREGATION_RULE_DATA.matchExpression1,
  triggerExpression: AGGREGATION_RULE_DATA.matchExpression2,
  tuningExpressions: [],
};

export const firstSeenRuleData = {
  ...SHARED_RULE_DATA,
  id: FIRST_SEEN_RULE_DATA.ruleId,
  ruleType: FIRST_SEEN_RULE_DATA.ruleType,
  name: FIRST_SEEN_RULE_DATA.name,
  baselineStatus: BaselineStatus.InProgress,
  baselineType: FirstSeenRuleBaselineType.Global,
  baselineWindowSize:
    FIRST_SEEN_RULE_DATA.baselineWindowSize * ONE_DAY_MILLISECONDS,
  retentionWindowSize:
    FIRST_SEEN_RULE_DATA.retentionWindowSize * ONE_DAY_MILLISECONDS,
  filterExpression: FIRST_SEEN_RULE_DATA.filterExpression,
  valueFields: FIRST_SEEN_RULE_DATA.valueFields,
  valueExpression: null,
  tuningExpressions: [],
  groupByFields: [],
};

export const updatedFirstSeenRuleData = {
  ...firstSeenRuleData,
  name: FIRST_SEEN_RULE_DATA.updatedName,
  score: FIRST_SEEN_RULE_DATA.updatedScore,
  scoreOverride: anIntRuleOverride({
    original: SHARED_RULE_DATA.score,
    override: FIRST_SEEN_RULE_DATA.updatedScore,
  }),
  baselineType: FirstSeenRuleBaselineType.PerEntity,
  groupByFields: FIRST_SEEN_RULE_DATA.groupByFields,
};

export const outlierRuleData = {
  ...SHARED_RULE_DATA,
  id: OUTLIER_RULE_DATA.ruleId,
  ruleType: OUTLIER_RULE_DATA.ruleType,
  name: OUTLIER_RULE_DATA.name,
  matchExpression: OUTLIER_RULE_DATA.matchExpression,
  baselineStatus: BaselineStatus.InProgress,
  baselineWindowSize:
    OUTLIER_RULE_DATA.baselineWindowSize * ONE_DAY_MILLISECONDS,
  retentionWindowSize:
    OUTLIER_RULE_DATA.retentionWindowSize * ONE_DAY_MILLISECONDS,
  windowSize: ONE_DAY_MILLISECONDS,
  windowSizeName: 'T24H',
  deviationThreshold: 3,
  floorValue: 2,
  tuningExpressions: [],
  groupByFields: OUTLIER_RULE_DATA.groupByFields,
  aggregationFunctions: [
    anOutlierRuleAggregationFunction({
      arguments: [OUTLIER_RULE_DATA.aggregationFunctionArg],
      function: OUTLIER_RULE_DATA.aggregationFunction,
    }),
  ],
};

export const OUTLIER_RULE_DATA_UPDATED = {
  ...outlierRuleData,
  name: OUTLIER_RULE_DATA.updatedName,
  score: OUTLIER_RULE_DATA.updatedScore,
  scoreOverride: anIntRuleOverride({
    original: SHARED_RULE_DATA.score,
    override: OUTLIER_RULE_DATA.updatedScore,
  }),
  aggregationFunctions: [
    anOutlierRuleAggregationFunction({
      arguments: [OUTLIER_RULE_DATA.updatedAggregationFunctionArg],
      function: OUTLIER_RULE_DATA.updatedAggregationFunction,
    }),
  ],
  baselineWindowSize:
    OUTLIER_RULE_DATA.updatedBaselineWindowSize * ONE_DAY_MILLISECONDS,
  retentionWindowSize:
    OUTLIER_RULE_DATA.updatedRetentionWindowSize * ONE_DAY_MILLISECONDS,
};

export const ruleBuilderQuery_chain = {
  __typename: 'Query',
  // @ts-ignore windowSizeInMillisecnds is aliased which causes issues with typing since MSW doesn't handle this case
  rule: aChainRule({
    ...chainRuleData,
  }),
};

export const ruleBuilderQuery_chain_updated = {
  __typename: 'Query',
  // @ts-ignore windowSizeInMillisecnds is aliased which causes issues with typing since MSW doesn't handle this case
  rule: aChainRule({
    ...chainRuleData,
    name: CHAIN_RULE_DATA.updatedName,
    score: CHAIN_RULE_DATA.updatedScore,
    scoreOverride: anIntRuleOverride({
      original: SHARED_RULE_DATA.score,
      override: CHAIN_RULE_DATA.updatedScore,
    }),
  }),
};

export const ruleBuilderQuery_match = {
  __typename: 'Query',
  rule: aTemplatedMatchRule({
    ...matchRuleData,
    scoreMapping: aRuleScoreMapping({
      type: 'constant',
      default: SHARED_RULE_DATA.score,
    }),
  }),
};

export const ruleBuilderQuery_match_updated = {
  __typename: 'Query',
  rule: aTemplatedMatchRule({
    ...matchRuleData,
    name: MATCH_RULE_DATA.updatedName,
    scoreMapping: aRuleScoreMapping({
      type: 'constant',
      default: MATCH_RULE_DATA.updatedScore,
    }),
  }),
};

export const ruleBuilderQuery_threshold = {
  __typename: 'Query',
  // @ts-ignore windowSizeInMillisecnds is aliased which causes issues with typing since MSW doesn't handle this case
  rule: aThresholdRule({
    ...thresholdRuleData,
  }),
};

export const ruleBuilderQuery_threshold_updated = {
  __typename: 'Query',
  // @ts-ignore windowSizeInMillisecnds is aliased which causes issues with typing since MSW doesn't handle this case
  rule: aThresholdRule({
    ...thresholdRuleData,
    name: THRESHOLD_RULE_DATA.updatedName,
    score: THRESHOLD_RULE_DATA.updatedScore,
    scoreOverride: anIntRuleOverride({
      original: SHARED_RULE_DATA.score,
      override: THRESHOLD_RULE_DATA.updatedScore,
    }),
  }),
};

export const ruleBuilderQuery_threshold_disabled =
  mockRuleBuilderQueryOperationQuery(
    async ({ query: _query, variables: _variables }) => {
      await delay(1500);
      return HttpResponse.json({
        data: {
          __typename: 'Query',
          // @ts-ignore windowSizeInMillisecnds is aliased which causes issues with typing since MSW doesn't handle this case
          rule: aThresholdRule({
            ...thresholdRuleData,
            name: THRESHOLD_RULE_DATA.updatedName,
            score: THRESHOLD_RULE_DATA.updatedScore,
            scoreOverride: anIntRuleOverride({
              original: SHARED_RULE_DATA.score,
              override: THRESHOLD_RULE_DATA.updatedScore,
            }),
            enabled: false,
          }),
        },
      });
    }
  );

export const ruleBuilderQuery_aggregation = {
  __typename: 'Query',
  // @ts-ignore windowSizeInMillisecnds is aliased which causes issues with typing since MSW doesn't handle this case
  rule: anAggregationRule({
    ...aggregationRuleData,
    aggregationFunctions: [
      anAggregationRuleAggregationFunction({
        arguments: [AGGREGATION_RULE_DATA.functionExpression],
        function: 'count',
      }),
    ],
    scoreMapping: aRuleScoreMapping({
      type: 'constant',
      default: SHARED_RULE_DATA.score,
    }),
  }),
};

export const ruleBuilderQuery_aggregation_updated = {
  __typename: 'Query',
  // @ts-ignore windowSizeInMillisecnds is aliased which causes issues with typing since MSW doesn't handle this case
  rule: anAggregationRule({
    ...aggregationRuleData,
    aggregationFunctions: [
      anAggregationRuleAggregationFunction({
        arguments: [AGGREGATION_RULE_DATA.functionExpression],
        function: 'count',
      }),
    ],
    name: AGGREGATION_RULE_DATA.updatedName,
    scoreMapping: aRuleScoreMapping({
      type: 'constant',
      default: AGGREGATION_RULE_DATA.updatedScore,
    }),
  }),
};

export const ruleBuilderQuery_firstSeen = {
  __typename: 'Query',
  rule: aFirstSeenRule({
    ...firstSeenRuleData,
  }),
};

export const ruleBuilderQuery_firstSeen_updated = {
  __typename: 'Query',
  rule: aFirstSeenRule({
    ...updatedFirstSeenRuleData,
  }),
};

export const ruleBuilderQuery_firstSeen_updated_failed = {
  __typename: 'Query',
  rule: aFirstSeenRule({
    ...updatedFirstSeenRuleData,
    baselineStatus: BaselineStatus.Failed,
  }),
};

export const ruleBuilderQuery_firstSeen_updated_too_many_records = {
  __typename: 'Query',
  rule: aFirstSeenRule({
    ...updatedFirstSeenRuleData,
    baselineStatus: BaselineStatus.TooManyRecords,
  }),
};

export const ruleBuilderQuery_firstSeen_updated_completed = {
  __typename: 'Query',
  rule: aFirstSeenRule({
    ...updatedFirstSeenRuleData,
    baselineStatus: BaselineStatus.Completed,
  }),
};

export const ruleBuilderQuery_outlier = {
  __typename: 'Query',
  rule: anOutlierRule({
    ...outlierRuleData,
  }),
};

export const ruleBuilderQuery_outlier_updated = {
  __typename: 'Query',
  rule: anOutlierRule({
    ...OUTLIER_RULE_DATA_UPDATED,
  }),
};

export const ruleBuilderQuery_outlier_updated_failed = {
  __typename: 'Query',
  rule: anOutlierRule({
    ...OUTLIER_RULE_DATA_UPDATED,
    baselineStatus: BaselineStatus.Failed,
  }),
};

export const ruleBuilderQuery_outlier_updated_completed = {
  __typename: 'Query',
  rule: anOutlierRule({
    ...OUTLIER_RULE_DATA_UPDATED,
    baselineStatus: BaselineStatus.Completed,
  }),
};

export const ruleBuilderQuery_outlier_updated_too_many_records = {
  __typename: 'Query',
  rule: anOutlierRule({
    ...OUTLIER_RULE_DATA_UPDATED,
    baselineStatus: BaselineStatus.TooManyRecords,
  }),
};

export const handlers = [
  mockRuleBuilderQueryOperationQuery(
    async ({ query: _query, variables: _variables }) => {
      await delay(1500);
      return HttpResponse.json({
        data: {
          __typename: 'Query',
          // @ts-ignore windowSizeInMillisecnds is aliased which causes issues with typing since MSW doesn't handle this case
          rule: anAggregationRule({
            ...SHARED_RULE_DATA,
            ruleType: 'aggregation',
          }),
        },
      });
    }
  ),
];
